import * as Sentry from '@sentry/react';
import { createFileRoute } from '@tanstack/react-router';
import jwtDecode from 'jwt-decode';

import type { RmClaims } from 'src/api/queries/user';
import { AccountLinkingMismatchError } from 'src/utils/errors';
import { LogCategory } from 'src/utils/logger.categories';
import { userHelpers } from 'src/utils/userHelpers';

export const Route = createFileRoute('/_auth/enterprise/enroll')({
  async beforeLoad({ context }) {
    const auth = await context.auth;

    if (!auth.user) {
      Sentry.addBreadcrumb({
        category: LogCategory.Auth,
        message: 'Enterprise enrollment: User not found',
      });
      throw new Error('Unauthorized');
    }

    if (!auth.user.email) {
      Sentry.addBreadcrumb({
        category: LogCategory.Auth,
        message: 'Enterprise enrollment: Email not found',
      });
      throw new Error('Email not found');
    }

    if (context.accountLinkingToken) {
      let previousUser: RmClaims | undefined;

      try {
        previousUser = jwtDecode<RmClaims>(context.accountLinkingToken);
      } catch (error) {
        Sentry.addBreadcrumb({
          category: LogCategory.Auth,
          message:
            'Enterprise enrollment: Unable to parse account linking token',
        });
        throw new Error('Invalid account linking token');
      }

      if (!previousUser) {
        throw new Error('Previous user not found');
      }

      let linkableAccountTarget: string | undefined;
      let accountToMatch: string | undefined;

      if (userHelpers.isSAMLUser(auth.user)) {
        linkableAccountTarget = userHelpers.getLinkableUserId(auth.user);
        accountToMatch = previousUser.sub;
      } else {
        linkableAccountTarget = userHelpers.getLinkableUserId(previousUser);
        accountToMatch = auth.user.sub;
      }

      if (linkableAccountTarget !== accountToMatch) {
        throw new AccountLinkingMismatchError(
          '/enterprise/enroll/verify-saml?accountLinkingError=Account%20mismatch',
          auth.user,
          previousUser
        );
      }
    }
  },
});
